import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { apiJson, apiFetch } from "../../api/http";

type Entitlement = {
  enforced: boolean;
  active: boolean;
  status: string;
  plan: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
};

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * In-pane subscription management shown in the expanded sidebar. Lets an active
 * subscriber cancel (at period end) or resume — no redirect to Stripe's portal.
 */
export default function SubscriptionPanel() {
  const [ent, setEnt] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEnt(await apiJson<Entitlement>("/api/me/entitlement"));
      setError(null);
    } catch {
      setEnt(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (path: string) => {
      setBusy(true);
      setError(null);
      try {
        const resp = await apiFetch(path, { method: "POST" });
        if (!resp.ok) throw new Error();
        await load();
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  if (loading) {
    return (
      <div className="px-3 py-2 text-xs text-gray-400">Loading subscription…</div>
    );
  }

  // Only meaningful when billing is enforced and there is a subscription record.
  if (!ent || !ent.enforced) return null;

  const active = ent.active;
  const canceling = ent.cancelAtPeriodEnd;
  const renews = fmtDate(ent.currentPeriodEnd);

  return (
    <div className="px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
        Subscription
      </p>

      <div className="rounded-md border border-gray-200 p-3">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              active ? "bg-brand-500" : "bg-gray-300"
            }`}
          />
          <span className="text-sm font-medium text-gray-900">
            {active ? (canceling ? "Active — ending" : "Active") : "Inactive"}
          </span>
        </div>

        {active && renews && (
          <p className="text-xs text-gray-500 mt-1">
            {canceling ? `Ends on ${renews}` : `Renews ${renews}`}
          </p>
        )}

        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

        {active && !canceling && (
          <button
            type="button"
            disabled={busy}
            onClick={() => act("/api/billing/cancel")}
            className="mt-3 w-full rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 px-3 py-1.5 transition-colors"
          >
            {busy ? "Working…" : "Cancel subscription"}
          </button>
        )}

        {active && canceling && (
          <button
            type="button"
            disabled={busy}
            onClick={() => act("/api/billing/resume")}
            className="mt-3 w-full rounded-md bg-brand-500 hover:bg-brand-600 text-white text-sm disabled:opacity-50 px-3 py-1.5 transition-colors"
          >
            {busy ? "Working…" : "Resume subscription"}
          </button>
        )}
      </div>
    </div>
  );
}
